import React from 'react';
import { AUDIENCES } from './audience';

/**
 * График одной меры платформы по артборду: столбики, высота 180, зазор
 * зависит от числа колонок. Линий по значениям нет намеренно — артборд рисует
 * именно столбики, и по ним лучше видно провалы выходных и ночную тишину.
 * Единственная линия — пунктир среднего; под графиком мин · макс · среднее.
 * Столбик складывается из групп «Кто» (`by_audience`, решение владельца
 * 14.10.2026) в цветах кнопок фильтра; ряд без разреза — одним цветом.
 */

export const MEASURES = [
  ['minutes', 'Минуты аудио', (b) => Math.round((b.audio_seconds || 0) / 60), 'мин'],
  ['tasks', 'Задачи', (b) => b.tasks || 0, 'задач'],
  ['users', 'Активные пользователи', (b) => b.active_users || 0, 'человек'],
  // Не из задач: учётки по дате создания и встречи, куда позвали бота.
  // Выбранный сервис их не сужает (просьба владельца 05.10).
  ['new_users', 'Новые пользователи', (b) => b.new_users || 0, 'человек', true],
  ['meetings', 'Встречи с ботом', (b) => b.meetings || 0, 'встреч', true],
];

const PER_GRAIN = { hour: 'в час', day: 'в день', week: 'в неделю' };
const CURRENT_GRAIN = { hour: 'часа', day: 'дня', week: 'недели' };

function tick(value, granularity) {
  if (!value) return '';
  if (granularity === 'hour') return String(value).slice(11, 16);
  const d = String(value).slice(0, 10).split('-');
  return d.length === 3 ? `${d[2]}.${d[1]}` : value;
}

/**
 * Мин / макс / среднее по ряду. Последняя корзина — текущий час, день или
 * неделя, она не закончилась и тянула бы минимум и среднее вниз, поэтому при
 * трёх и более корзинах не считается (`partial: true`).
 */
export function stats(values) {
  if (!values || values.length === 0) return null;
  const partial = values.length >= 3;
  const full = partial ? values.slice(0, -1) : values;
  const sum = full.reduce((acc, v) => acc + v, 0);
  return {
    min: Math.min(...full),
    max: Math.max(...full),
    avg: Math.round(sum / full.length),
    partial,
  };
}

const fmt = (n) => n.toLocaleString('ru-RU');

export default function PlatformChart({ buckets, granularity, measure, title }) {
  const spec = MEASURES.find(([id]) => id === measure) || MEASURES[0];
  const [, label, read, unit] = spec;
  const values = (buckets || []).map((b) => ({
    date: b.date,
    value: read(b),
    // Снизу вверх в порядке кнопок; пустые доли не рисуем.
    parts: b.by_audience
      ? AUDIENCES.filter(([id]) => b.by_audience[id])
        .map(([id, name, color]) => ({ id, name, color, value: read(b.by_audience[id]) }))
        .filter((p) => p.value > 0)
      : null,
  }));
  const max = Math.max(1, ...values.map((v) => v.value));
  const s = stats(values.map((v) => v.value));
  const gap = values.length > 40 ? 'gap-[2px]' : values.length > 12 ? 'gap-[3px]' : 'gap-2';
  // Подписи по оси: на длинном ряду каждая вторая-четвёртая, иначе они слипаются.
  const step = values.length > 40 ? 6 : values.length > 20 ? 3 : values.length > 12 ? 2 : 1;

  return (
    <section className="flex flex-col gap-4 rounded-card bg-paper-card px-6 py-[22px]">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="text-[12px] text-paper-ink-3">{title || label}</span>
        {s && (
          <span className="font-mono text-[12px] text-paper-hint">
            мин {fmt(s.min)} · макс {fmt(s.max)} · среднее {fmt(s.avg)} {unit} {PER_GRAIN[granularity] || ''}
            {s.partial ? ` · без текущего ${CURRENT_GRAIN[granularity] || 'шага'}` : ''}
          </span>
        )}
      </div>

      {values.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-paper-ink-3">
          Нет данных за выбранный период
        </div>
      ) : (
        <>
          <div className="relative">
            <div className={`flex h-[180px] items-end ${gap}`}>
              {values.map((v) => (
                <div key={v.date} title={`${tick(v.date, granularity)}: ${fmt(v.value)} ${unit}`}
                     className="flex h-full flex-1 flex-col justify-end">
                  {v.parts && v.parts.length > 0 ? (
                    <div className="flex w-full flex-col-reverse overflow-hidden rounded-[6px]"
                         style={{ height: `${Math.max(2, Math.round((v.value / max) * 100))}%` }}>
                      {v.parts.map((p) => (
                        <div key={p.id} data-audience={p.id}
                             title={`${tick(v.date, granularity)} · ${p.name}: ${fmt(p.value)} ${unit}`}
                             style={{ flexGrow: p.value, flexBasis: 0, background: p.color }} />
                      ))}
                    </div>
                  ) : (
                    <div className={`w-full rounded-[6px] ${v.value ? 'bg-paper-ink' : 'bg-paper-bg'}`}
                         style={{ height: `${Math.max(2, Math.round((v.value / max) * 100))}%` }} />
                  )}
                </div>
              ))}
            </div>
            {s && s.avg > 0 && (
              <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-paper-accent/70"
                   style={{ bottom: `${Math.min(100, Math.round((s.avg / max) * 100))}%` }}
                   title={`Среднее — ${fmt(s.avg)} ${unit}`}>
                <span className="absolute right-0 -top-[7px] rounded-[4px] bg-paper-card px-1 font-mono text-[10px] leading-[14px] text-paper-accent">
                  {fmt(s.avg)}
                </span>
              </div>
            )}
          </div>

          <div className={`flex ${gap} font-mono text-[11px] text-paper-hint`}>
            {values.map((v, i) => (
              <span key={v.date} className="flex-1 overflow-hidden text-center whitespace-nowrap">
                {i % step === 0 ? tick(v.date, granularity) : ''}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
